// @flow
import { createSelector } from 'reselect';
import { Map as ImmutableMap } from 'immutable';

import { getPureManualAccounts } from 'data/accounts/selectors';
import { getTopTierEntitlement } from 'data/entitlements/entitlementsSelectors';
import { getInstitutionsById } from 'data/institutions/selectors';

import buildUpsertableAccountData from './buildUpsertableAccountData';
import { REDUCER_KEY } from './reducer';
import type { InstitutionLoginUpserterStore } from './types';
import * as transformers from './transformers';

export const getInstitutionLoginUpserterStore = (state: any): InstitutionLoginUpserterStore =>
  state[REDUCER_KEY];

export const getInstitutionId = (state: any) => getInstitutionLoginUpserterStore(state).institutionId;
export const getInstitutionLogin = (state: any) => getInstitutionLoginUpserterStore(state).institutionLogin;
export const getMfaChallenge = (state: any) => getInstitutionLoginUpserterStore(state).mfaChallenge;
export const getPartnerAuthUris = (state: any) => getInstitutionLoginUpserterStore(state).partnerAuthUris;
export const getPublicToken = (state: any) => getInstitutionLoginUpserterStore(state).publicToken;

export const getAccountDiscoveryData = (state: any) => getInstitutionLoginUpserterStore(state).accountDiscoveryData;
export const getAccountDiscoveryDataAsJS = createSelector(
  getAccountDiscoveryData,
  (data) => data ? data.toJS() : null
);

export const getIsAuthenticationDone = (state: any) => getInstitutionLoginUpserterStore(state).isAuthenticationDone;
export const getIsAccountUpsertingDone = (state: any) => getInstitutionLoginUpserterStore(state).isAccountUpsertingDone;
export const getIsAccountRefreshingDone = (state: any) => getInstitutionLoginUpserterStore(state).isAccountRefreshingDone;

export const getIsPolling = (state: any) => getInstitutionLoginUpserterStore(state).isPolling;
export const getIsSubmitting = (state: any) => getInstitutionLoginUpserterStore(state).isSubmitting;

export const getError = (state: any) => getInstitutionLoginUpserterStore(state).error;

export const getInstitution = createSelector(
  getInstitutionsById,
  (state, _props) => getInstitutionId(state),
  (institutionsById, institutionId) =>
    institutionId != null ? institutionsById.get(institutionId) : null
);

export const getUpsertableAccountData = createSelector(
  getAccountDiscoveryData,
  getPureManualAccounts,
  getTopTierEntitlement,
  (accountDiscoveryData, pureManualAccounts, tier) => {
    const accountsWithoutGoals = pureManualAccounts.filter((acct) => acct.type !== 'GOAL');
    return buildUpsertableAccountData(accountDiscoveryData, accountsWithoutGoals, tier);
  }
);

export const getAccountDiscoveryDataForTesting = createSelector(
  () => {
    const data = {
      institutionLoginId: '126817258656712194',
      mode: ' DISCOVER_AND_ADD_ACCOUNTS',
      status: 'OK',
      aggregators: [
        {
          aggStatus: 'DISCOVERED_ACCOUNTS_READY_FOR_USER',
          isProcessing: false,
        },
      ],
      accounts: [
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315190',
              cpDetails: {
                type: 'INVESTMENT',
                subType: 'INVESTMENT_TAXABLE',
                name: 'My Brokerage',
                maskedNumber: '0000000000',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'INR',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315191',
              cpDetails: {
                type: 'LOAN',
                subType: 'MORTGAGE',
                name: 'My Mortgage',
                maskedNumber: '1000000001',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'BND',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315193',
              cpDetails: {
                type: 'CREDIT',
                subType: 'CREDIT_CARD',
                name: 'My Visa',
                maskedNumber: '4100007777',
                balance: -805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'USD',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315185',
              cpDetails: {
                type: 'BANK',
                subType: 'CD',
                name: 'My CD',
                maskedNumber: '2000005555',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'INR',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315186',
              cpDetails: {
                type: 'INVESTMENT',
                subType: 'INVESTMENT_TAXABLE',
                name: 'My Roth IRA',
                maskedNumber: '2000004444',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'USD',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315189',
              cpDetails: {
                type: 'BANK',
                subType: 'SAVINGS',
                name: 'My Savings',
                maskedNumber: '1000002222',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'INR',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315192',
              cpDetails: {
                type: 'INVESTMENT',
                subType: 'INVESTMENT_TAX_DEFERRED',
                name: 'My Retirement',
                maskedNumber: '0000000001',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'INR',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315194',
              cpDetails: {
                type: 'BANK',
                subType: 'CHECKING',
                name: 'My Checking',
                maskedNumber: '1000001111',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'BND',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315187',
              cpDetails: {
                type: 'LOAN',
                subType: 'LOAN',
                name: 'My Auto Loan',
                maskedNumber: '8000008888',
                balance: 805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'USD',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
        {
          aggregators: [
            {
              channel: 'FDS_SCREEN_SCRAPED',
              cpId: '2906315188',
              cpDetails: {
                type: 'CREDIT',
                subType: 'LINE_OF_CREDIT',
                name: 'My Line of Credit',
                maskedNumber: '8000006666',
                balance: -805.09,
                balanceAt: '2018-08-05T07:00:00Z',
                currency: 'INR',
                aggStatus: 'OK',
                cpAggStatusCode: 'success',
              },
            },
          ],
        },
      ],
      isProcessing: false,
    };
    return transformers.transformResponseToAccountDiscoveryData(data, ImmutableMap());
  }
);
